/**
 * Permission codes the Bank Reconciliation screens declare an interest in.
 *
 * They are the dot-strings `internal/bankrec/permissions.go` registers with the
 * RBAC catalogue (CLAUDE.md §6) and that arrive on `/auth/me`. Naming them once
 * here means a renamed permission is a compile error rather than a menu that
 * silently disappears.
 *
 * The four at the bottom belong to other modules and are used the way
 * `internal/bankrec` itself treats those tables — as things to *read*. A user
 * without them can still reconcile: the account picker falls back to naming an
 * account by its id, and the create-missing-entry action hides rather than
 * offering a form it cannot fill. Reconciliation must not require the ability
 * to edit a bank account or the chart of accounts.
 */

export const BANKREC_PERMISSIONS = {
  profileRead: 'bankrec.profile.read',
  profileManage: 'bankrec.profile.manage',

  statementImport: 'bankrec.statement.import',
  statementRead: 'bankrec.statement.read',

  /** Ask the engine what it thinks a statement line is. Writes nothing. */
  matchSuggest: 'bankrec.match.suggest',
  /** Turn a suggestion — or a manual pick — into a match, and post a missing entry. */
  matchConfirm: 'bankrec.match.confirm',
  matchRelease: 'bankrec.match.release',

  sessionRead: 'bankrec.session.read',
  /** Open and close a reconciliation session, and request the reopening of a closed one. */
  sessionManage: 'bankrec.session.manage',

  /* --------------------------------------------------- C4: reconciliation depth */

  /** Create and supersede standing charge / interest rules. */
  standingRuleManage: 'bankrec.standing_rule.manage',
  /**
   * Set a standing rule to post **unattended**. Registered `RequiresMFA`, which
   * is why the `auto` option is absent rather than disabled for a user without
   * it — a greyed-out choice invites a hunt for the switch that enables it.
   */
  standingRuleAuto: 'bankrec.standing_rule.auto',
  /** Certify a session and build its certificate. */
  sessionCertify: 'bankrec.session.certify',
  /** Be the *second* authoriser on a reopen — never the requester. */
  sessionReopenAuthorise: 'bankrec.session.reopen.authorise',

  /** Name the bank account a session, an import and a statement line belong to. */
  bankAccountRead: 'cash.account.read',
  /** Scope the profile list, and label a session's company. */
  companyRead: 'org.company.read',
  /** Offer the offset account a bank charge or credit is posted against. */
  glAccountRead: 'org.account.read',
  /** Open the journal behind a matched ledger line. */
  journalRead: 'gl.journal.read',
} as const

export type BankrecPermission = (typeof BANKREC_PERMISSIONS)[keyof typeof BANKREC_PERMISSIONS]
